"use client";

import { c as _c } from "react/compiler-runtime";
import * as MapLibreGL from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { createContext, forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { cn } from "#/lib/utils";
import "./maplibre-worker";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Check document class for theme (works with next-themes, etc.)
function getDocumentTheme() {
  if (typeof document === "undefined") return null;
  if (document.documentElement.classList.contains("dark")) return "dark";
  if (document.documentElement.classList.contains("light")) return "light";
  return null;
}

// Get system preference
function getSystemTheme() {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
function useResolvedTheme(themeProp) {
  const $ = _c(3);
  const [detectedTheme, setDetectedTheme] = useState(_temp);
  let t0;
  let t1;
  if ($[0] !== themeProp) {
    t0 = () => {
      if (themeProp) {
        return;
      }
      const observer = new MutationObserver(() => {
        const docTheme = getDocumentTheme();
        if (docTheme) {
          setDetectedTheme(docTheme);
        }
      });
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["class"]
      });
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleSystemChange = e => {
        if (!getDocumentTheme()) {
          setDetectedTheme(e.matches ? "dark" : "light");
        }
      };
      mediaQuery.addEventListener("change", handleSystemChange);
      return () => {
        observer.disconnect();
        mediaQuery.removeEventListener("change", handleSystemChange);
      };
    };
    t1 = [themeProp];
    $[0] = themeProp;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return themeProp ?? detectedTheme;
}
function _temp() {
  return getDocumentTheme() ?? getSystemTheme();
}
const MapContext = /*#__PURE__*/createContext(null);
function useMap() {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error("useMap must be used within a Map component");
  }
  return context;
}

/** Map viewport state */

function DefaultLoader() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = /*#__PURE__*/_jsx("div", {
      className: "bg-background/50 absolute inset-0 z-10 flex items-center justify-center backdrop-blur-xs",
      children: /*#__PURE__*/_jsxs("div", {
        className: "flex gap-1",
        children: [/*#__PURE__*/_jsx("span", {
          className: "bg-muted-foreground/60 size-1.5 animate-pulse rounded-full"
        }), /*#__PURE__*/_jsx("span", {
          className: "bg-muted-foreground/60 size-1.5 animate-pulse rounded-full [animation-delay:150ms]"
        }), /*#__PURE__*/_jsx("span", {
          className: "bg-muted-foreground/60 size-1.5 animate-pulse rounded-full [animation-delay:300ms]"
        })]
      })
    });
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function getViewport(map) {
  const center = map.getCenter();
  return {
    center: [center.lng, center.lat],
    zoom: map.getZoom(),
    bearing: map.getBearing(),
    pitch: map.getPitch()
  };
}
const Map = /*#__PURE__*/forwardRef(function Map({
  children,
  className,
  theme: themeProp,
  styles,
  projection,
  viewport,
  onViewportChange,
  loading = false,
  ...props
}, ref) {
  const containerRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isStyleLoaded, setIsStyleLoaded] = useState(false);
  const [initFailed, setInitFailed] = useState(false);
  const currentStyleRef = useRef(null);
  const styleTimeoutRef = useRef(null);
  const internalUpdateRef = useRef(false);
  const resolvedTheme = useResolvedTheme(themeProp);
  const isControlled = viewport !== undefined && onViewportChange !== undefined;
  const onViewportChangeRef = useRef(onViewportChange);
  // Latest-callback ref synced during render on purpose (read only from map event handlers).
  // eslint-disable-next-line react-hooks/refs
  onViewportChangeRef.current = onViewportChange;
  const mapStyles = useMemo(() => ({
    light: styles.light,
    dark: styles.dark ?? styles.light
  }), [styles]);
  useImperativeHandle(ref, () => mapInstance, [mapInstance]);
  const clearStyleTimeout = useCallback(() => {
    if (styleTimeoutRef.current) {
      clearTimeout(styleTimeoutRef.current);
      styleTimeoutRef.current = null;
    }
  }, []);

  // Initialize the map
  useEffect(() => {
    if (!containerRef.current) return;
    const initialStyle = resolvedTheme === "dark" ? mapStyles.dark : mapStyles.light;
    currentStyleRef.current = initialStyle;

    // MapLibre throws here when WebGL is unavailable. Only this failure gets the
    // map fallback; errors from children propagate to the surrounding boundary.
    let map;
    try {
      map = new MapLibreGL.Map({
        container: containerRef.current,
        style: initialStyle,
        renderWorldCopies: false,
        attributionControl: {
          compact: true
        },
        ...props,
        ...viewport
      });
    } catch (error) {
      console.error("Map failed to initialize:", error);
      // WebGL support is only known after attempting to create the map.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setInitFailed(true);
      return;
    }
    const styleDataHandler = () => {
      clearStyleTimeout();
      styleTimeoutRef.current = setTimeout(() => {
        setIsStyleLoaded(true);
        if (projection) {
          map.setProjection(projection);
        }
      }, 100);
    };
    const loadHandler = () => setIsLoaded(true);
    const handleMove = () => {
      if (internalUpdateRef.current) return;
      onViewportChangeRef.current?.(getViewport(map));
    };
    map.on("load", loadHandler);
    map.on("styledata", styleDataHandler);
    map.on("move", handleMove);
    setMapInstance(map);
    return () => {
      clearStyleTimeout();
      map.off("load", loadHandler);
      map.off("styledata", styleDataHandler);
      map.off("move", handleMove);
      map.remove();
      setIsLoaded(false);
      setIsStyleLoaded(false);
      setMapInstance(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync controlled viewport to map
  useEffect(() => {
    if (!mapInstance || !isControlled || !viewport) return;
    if (mapInstance.isMoving()) return;
    const current = getViewport(mapInstance);
    const next = {
      center: viewport.center ?? current.center,
      zoom: viewport.zoom ?? current.zoom,
      bearing: viewport.bearing ?? current.bearing,
      pitch: viewport.pitch ?? current.pitch
    };
    if (next.center[0] === current.center[0] && next.center[1] === current.center[1] && next.zoom === current.zoom && next.bearing === current.bearing && next.pitch === current.pitch) {
      return;
    }
    internalUpdateRef.current = true;
    mapInstance.jumpTo(next);
    internalUpdateRef.current = false;
  }, [mapInstance, isControlled, viewport]);

  // Handle style change
  useEffect(() => {
    if (!mapInstance || !resolvedTheme) return;
    const newStyle = resolvedTheme === "dark" ? mapStyles.dark : mapStyles.light;
    if (currentStyleRef.current === newStyle) return;
    clearStyleTimeout();
    currentStyleRef.current = newStyle;
    setIsStyleLoaded(false);
    mapInstance.setStyle(newStyle, {
      diff: true
    });
  }, [mapInstance, resolvedTheme, mapStyles, clearStyleTimeout]);
  const contextValue = useMemo(() => ({
    map: mapInstance,
    isLoaded: isLoaded && isStyleLoaded
  }), [mapInstance, isLoaded, isStyleLoaded]);
  if (initFailed) return /*#__PURE__*/_jsx(MapErrorFallback, {
    className: className
  });
  return /*#__PURE__*/_jsx(MapContext.Provider, {
    value: contextValue,
    children: /*#__PURE__*/_jsxs("div", {
      ref: containerRef,
      className: cn("relative h-full w-full", className),
      children: [(!isLoaded || loading) && /*#__PURE__*/_jsx(DefaultLoader, {}), mapInstance && children]
    })
  });
});
function MapErrorFallback(t0) {
  const $ = _c(4);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("bg-muted text-muted-foreground flex h-full w-full items-center justify-center p-6 text-center text-sm", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = /*#__PURE__*/_jsx("div", {
      role: "alert",
      className: t1,
      children: "Kartan kunde inte laddas. Din webbl\xE4sare kanske inte st\xF6der WebGL, eller s\xE5 tappades anslutningen. Ladda om sidan f\xF6r att f\xF6rs\xF6ka igen."
    });
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
export { Map, MapContext, useMap };